/** Hero-sheet logic: the artifact doll, drop legality, army drags, experience.
 *
 * Pure. Nothing here decides an outcome (I10) - the screen still sends a
 * command and the kernel still rules on it. What these functions do is answer
 * "may I show this drop as legal *right now*", which is a presentation
 * question, and they answer it with the same test the kernel applies so the
 * greyed-out state and the kernel never disagree.
 */

import type {
  ArmyState, ArtifactDefId, ArtifactSlot, ContentRegistry, CreatureDefId,
  GameCommand, HeroState, Modifier, Reason,
} from "@game/core";
import {
  armyMoraleModifier, classModifiers, resolveStat, slotAccepts, slotIsFree, subskillById,
} from "@game/core";

// -------------------------------------------------------------------- doll

export interface DollCell {
  readonly slot: ArtifactSlot;
  readonly label: string;
  /** Grid position, 1-based, for `grid-column` / `grid-row`. */
  readonly col: number;
  readonly row: number;
}

/**
 * The 19 equipment slots, laid out as a figure rather than a list.
 *
 * The centre column reads head-to-foot; hands sit outside the shoulders; the
 * five misc sockets and the spellbook fill the flanks, and the four war-machine
 * sockets are a separate rank along the bottom because they are not worn.
 */
export const DOLL: readonly DollCell[] = Object.freeze([
  { slot: "head", label: "Head", col: 3, row: 1 },
  { slot: "shoulders", label: "Shoulders", col: 2, row: 2 },
  { slot: "neck", label: "Neck", col: 3, row: 2 },
  { slot: "misc1", label: "Miscellaneous", col: 4, row: 2 },
  { slot: "leftHand", label: "Left hand", col: 1, row: 3 },
  { slot: "torso", label: "Torso", col: 3, row: 3 },
  { slot: "rightHand", label: "Right hand", col: 5, row: 3 },
  { slot: "leftRing", label: "Left ring", col: 2, row: 4 },
  { slot: "feet", label: "Feet", col: 3, row: 4 },
  { slot: "rightRing", label: "Right ring", col: 4, row: 4 },
  { slot: "misc2", label: "Miscellaneous", col: 1, row: 5 },
  { slot: "misc3", label: "Miscellaneous", col: 2, row: 5 },
  { slot: "spellbook", label: "Spellbook", col: 3, row: 5 },
  { slot: "misc4", label: "Miscellaneous", col: 4, row: 5 },
  { slot: "misc5", label: "Miscellaneous", col: 5, row: 5 },
  { slot: "warMachine1", label: "War machine", col: 1, row: 6 },
  { slot: "warMachine2", label: "War machine", col: 2, row: 6 },
  { slot: "warMachine3", label: "War machine", col: 4, row: 6 },
  { slot: "warMachine4", label: "War machine", col: 5, row: 6 },
]);

export const ARTIFACT_SLOTS: readonly ArtifactSlot[] = Object.freeze(DOLL.map((d) => d.slot));

export const equippedIn = (h: HeroState, slot: string): ArtifactDefId | null =>
  h.artifacts[slot] ?? null;

// ---------------------------------------------------------------- legality

/**
 * Why the artifact at `backpackIndex` may not be dropped on `slot`.
 *
 * This mirrors `equip` in `adventure/execute.ts` exactly, including its odd
 * `SLOT_OCCUPIED{slot:0}` - the reason vocabulary numbers *army* slots, so the
 * artifact case has no number to report and the kernel sends 0. Copying the
 * quirk is deliberate: a tooltip that says something the kernel would not is
 * worse than a tooltip that says something slightly opaque.
 */
export function equipIllegality(
  content: ContentRegistry, hero: HeroState, backpackIndex: number, slot: string,
): Reason | null {
  const art = hero.backpack[backpackIndex];
  if (art === undefined) return { code: "SLOT_EMPTY", slot: backpackIndex };
  const def = content.artifacts.get(art);
  if (def === undefined) return { code: "NO_SUCH_ENTITY", id: art as string };
  if (!slotAccepts(def, slot)) return { code: "TARGET_INVALID" };
  if (!slotIsFree(hero, slot)) return { code: "SLOT_OCCUPIED", slot: 0 };
  return null;
}

export function unequipIllegality(hero: HeroState, slot: string): Reason | null {
  const art = hero.artifacts[slot];
  if (art === null || art === undefined) return { code: "SLOT_EMPTY", slot: 0 };
  return null;
}

/** The sockets an artifact could ever go in: its family, read off the kernel. */
export { slotsFor } from "@game/core";

// ------------------------------------------------------------ morale and luck

export interface Fortune {
  readonly value: number;
  /** Where it came from, in the order a player would read it. */
  readonly terms: readonly { readonly why: string; readonly value: number }[];
}

/** Every modifier a hero's own equipment and training put on one stat. */
function wornModifiers(
  content: ContentRegistry, hero: HeroState, stat: string,
): readonly { readonly mod: Modifier; readonly why: string }[] {
  const out: { mod: Modifier; why: string }[] = [];
  for (const s of hero.secondary) {
    const def = content.skills.get(s.def);
    if (def === undefined) continue;
    for (const m of def.levels[s.level - 1] ?? []) {
      if (m.stat === stat) out.push({ mod: m, why: def.name });
    }
    // A subskill is named for itself, not for its parent: "Forced March" is
    // what the player chose, and a row reading "Logistics" twice explains
    // nothing about where the second number came from.
    for (const id of s.picks ?? []) {
      const sub = subskillById(def, id);
      if (sub === undefined) continue;
      for (const m of sub.modifiers) if (m.stat === stat) out.push({ mod: m, why: sub.name });
    }
  }
  // Sorted so two states differing only in key order read the same (I3).
  for (const slot of Object.keys(hero.artifacts).sort()) {
    const id = hero.artifacts[slot];
    if (id === null || id === undefined) continue;
    const def = content.artifacts.get(id);
    if (def === undefined) continue;
    for (const m of def.modifiers) if (m.stat === stat) out.push({ mod: m, why: def.name });
  }
  // What the hero IS, beside what he wears and knows: the class passive
  // (a Knight's morale, a Fenstalker's luck) and the specialty's own flat
  // list. Both are content, so the sheet can read them without a kernel
  // context, and both reach the battle through `specialtyModifiers`.
  const hd = content.heroes.get(hero.def);
  if (hd !== undefined) {
    for (const m of classModifiers(content, hd)) {
      if (m.stat === stat) out.push({ mod: m, why: hd.heroClass });
    }
    for (const m of hd.specialty.modifiers) {
      if (m.stat === stat) out.push({ mod: m, why: "Speciality" });
    }
  }
  return out;
}

/**
 * The two numbers the classic prints beside Specialty, and the reasons for them.
 *
 * Morale is the interesting one, and it is the reason this is here rather than
 * in a tooltip: the kernel has computed it since the battle screen was written
 * - `armyMoraleModifier` in `battle/init.ts`, the classic's Section 24 rule that one
 * alignment is +1, two is neutral, each further one another -1, and undead
 * beside living costs a further point - and it was called from exactly one
 * place, inside battle setup. So the sheet that shows a player his seven
 * stacks would not tell him that mixing them had cost him a morale point until
 * the fight had already started.
 *
 * The army term is computed off the SAME function the battle uses rather than
 * restated here, for the same reason `equipIllegality` mirrors the kernel's
 * own test: a sheet that says something the rules would not is worse than a
 * sheet that says nothing.
 *
 * Luck has no army term in the classic - a mixed army is unlucky, not unfortunate -
 * so it is equipment and training only.
 */
export function heroFortune(
  content: ContentRegistry, hero: HeroState, army: ArmyState | null,
): { readonly morale: Fortune; readonly luck: Fortune } {
  const factions: string[] = [];
  let anyUndead = false;
  let anyLiving = false;
  let stacks = 0;
  for (const slot of army?.slots ?? []) {
    if (slot === null) continue;
    const def = content.creatures.get(slot.def);
    if (def === undefined) continue;
    stacks += 1;
    /* `init.ts` counts an undead stack as neither a faction nor a living one -
       an army of skeletons is one alignment, not two - so the same test is
       made here off the same ability rather than off a faction name. */
    if (def.abilities.some((a) => a.kind === "UNDEAD")) anyUndead = true;
    else { anyLiving = true; factions.push(def.faction as string); }
  }
  const armyTerm = stacks === 0 ? 0 : armyMoraleModifier(factions, anyUndead, anyLiving);

  const build = (stat: string, base: number, baseWhy: string | null): Fortune => {
    const worn = wornModifiers(content, hero, stat);
    // The battlefield clamps both readings to [-3, 3] before they are rolled
    // against, so a sheet that printed +5 would be promising a roll the rules
    // cannot make.
    const raw = resolveStat(base, worn.map((w) => w.mod)).value;
    const value = raw < -3 ? -3 : raw > 3 ? 3 : raw;
    const terms: { why: string; value: number }[] = [];
    if (baseWhy !== null && base !== 0) terms.push({ why: baseWhy, value: base });
    for (const w of worn) {
      if (w.mod.op === "add") terms.push({ why: w.why, value: w.mod.value });
    }
    return { value, terms };
  };

  return {
    morale: build("morale", armyTerm, armyTerm === 0 ? null : "Troops in this army"),
    luck: build("luck", 0, null),
  };
}

/** The word the classic puts on a morale or luck reading. */
export function fortuneWord(value: number): string {
  if (value <= -3) return "Cursed";
  if (value === -2) return "Awful";
  if (value === -1) return "Poor";
  if (value === 0) return "Neutral";
  if (value === 1) return "Good";
  if (value === 2) return "Great";
  return "Blessed";
}

// ---------------------------------------------------------------- experience

export function experienceAtLevel(content: ContentRegistry, level: number): number {
  const t = content.levels.thresholds;
  if (level <= 1) return 0;
  return t[level - 1] ?? t[t.length - 1] ?? 0;
}

export interface ExperienceBar {
  readonly level: number;
  readonly current: number;
  readonly thisLevel: number;
  readonly nextLevel: number;
  /** 0..10000 basis points through the current level; 10000 when maxed out. */
  readonly progressBp: number;
  readonly toNext: number;
  readonly maxed: boolean;
}

export function experienceBar(content: ContentRegistry, hero: HeroState): ExperienceBar {
  const t = content.levels.thresholds;
  const thisLevel = experienceAtLevel(content, hero.level);
  const maxed = hero.level >= t.length;
  const nextLevel = maxed ? thisLevel : experienceAtLevel(content, hero.level + 1);
  const span = nextLevel - thisLevel;
  const into = hero.experience - thisLevel;
  const progressBp = maxed || span <= 0
    ? 10000
    : Math.max(0, Math.min(10000, Math.floor((into * 10000) / span)));
  return {
    level: hero.level,
    current: hero.experience,
    thisLevel,
    nextLevel,
    progressBp,
    toNext: maxed ? 0 : Math.max(0, nextLevel - hero.experience),
    maxed,
  };
}

// -------------------------------------------------------------- army drags

export interface ArmyDrag {
  readonly fromArmy: ArmyState;
  readonly fromSlot: number;
  readonly toArmy: ArmyState;
  readonly toSlot: number;
  /** Shift-drag moves exactly one creature; a plain drag moves the stack. */
  readonly single: boolean;
  /** Set by the split dialog; overrides `single`. */
  readonly count?: number;
}

/**
 * The command a drop implies.
 *
 * Four cases, and the classic's rules for them: onto an empty slot in the same army
 * with a partial count is a split; onto a slot holding the same creature is a
 * merge; onto a slot holding a different creature is a swap; across armies it
 * is always a transfer, which the kernel splits or merges as needed. Returns
 * null when the drop is a no-op, so a caller can suppress the drag ghost.
 */
export function armyDropCommand(drag: ArmyDrag): GameCommand | null {
  const from = drag.fromArmy.slots[drag.fromSlot] ?? null;
  if (from === null) return null;
  const sameArmy = drag.fromArmy.id === drag.toArmy.id;
  if (sameArmy && drag.fromSlot === drag.toSlot) return null;

  const whole = from.count;
  const wanted = drag.count ?? (drag.single ? 1 : whole);
  const count = wanted < 1 ? 1 : wanted > whole ? whole : wanted;
  const target = drag.toArmy.slots[drag.toSlot] ?? null;

  if (!sameArmy) {
    return {
      type: "TRANSFER_STACK",
      fromArmy: drag.fromArmy.id,
      fromSlot: drag.fromSlot,
      toArmy: drag.toArmy.id,
      toSlot: drag.toSlot,
      count,
    };
  }
  if (target === null) {
    // Moving the WHOLE stack to a free socket is a swap with an empty one, not
    // a split: `splitStack` refuses `count >= src.count` (it would leave the
    // source empty, which is what a swap is for), so emitting a split here was
    // a command the kernel could only ever refuse - a whole-stack drag onto a
    // free socket did nothing at all.
    if (count >= whole) return { type: "SWAP_STACK", army: drag.fromArmy.id, a: drag.fromSlot, b: drag.toSlot };
    return { type: "SPLIT_STACK", army: drag.fromArmy.id, from: drag.fromSlot, to: drag.toSlot, count };
  }
  if (target.def === from.def) {
    return { type: "MERGE_STACK", army: drag.fromArmy.id, from: drag.fromSlot, to: drag.toSlot };
  }
  return { type: "SWAP_STACK", army: drag.fromArmy.id, a: drag.fromSlot, b: drag.toSlot };
}

/**
 * Where a whole stack of `def` should land when it is sent to `to` without a
 * drop target being named - the click-to-move path, which has a source socket
 * and no destination one.
 *
 * The classic's own order: a socket already holding that creature first, so sending
 * Archers to a hero who has Archers thickens the stack rather than taking up a
 * second socket; then the first free socket. `null` when the army is full of
 * seven other creatures, which is the one case the gesture cannot express and
 * the kernel would refuse anyway (`SLOT_OCCUPIED`).
 */
export function receivingSlot(to: ArmyState, def: CreatureDefId): number | null {
  for (let i = 0; i < to.slots.length; i++) {
    const s = to.slots[i] ?? null;
    if (s !== null && s.def === def) return i;
  }
  for (let i = 0; i < to.slots.length; i++) {
    if ((to.slots[i] ?? null) === null) return i;
  }
  return null;
}

/**
 * A stack picked up without a drag: the touch-screen half of the army rows.
 *
 * HTML5 drag events never fire on a touch screen, so on a phone a stack could
 * be looked at and never rearranged. The card that opens on a tap now offers
 * "Move" and "Split"; either puts the stack in the player's hand, and the next
 * tap on a socket is the drop. `count` is `null` for the whole stack and
 * `"ask"` when a dialog is to choose the number once the destination is known
 * (a split needs both ends before it can say how many may go).
 */
export interface Carry {
  readonly army: string;
  readonly slot: number;
  readonly count: number | "ask" | null;
}

export type CarryTap =
  /** The drop is one command, the same one a mouse drag would have issued. */
  | { readonly kind: "command"; readonly cmd: GameCommand }
  /** Both ends are known; a dialog has to choose the count. */
  | { readonly kind: "ask"; readonly from: Carry; readonly to: { readonly army: string; readonly slot: number } }
  /** Nothing happens; `why` is what to tell the player, if anything. */
  | { readonly kind: "noop"; readonly why: string | null };

/**
 * What a tap on `tap` does while `carry` is in hand.
 *
 * The rules are `armyDropCommand`'s - a tap is a drop that took two gestures
 * instead of one - with one addition: a split that still has to ask its count
 * cannot land on an occupied socket of its own army, because that drop would
 * be a merge or a swap and the dialog it opens would then promise "move N" and
 * do something else. Across armies every socket is fair, and the kernel says
 * which of transfer, merge or refusal it is.
 */
export function carryTap(
  carry: Carry, tap: { readonly army: string; readonly slot: number },
  armies: ReadonlyMap<string, ArmyState>,
): CarryTap {
  const from = armies.get(carry.army);
  const to = armies.get(tap.army);
  if (from === undefined || to === undefined) return { kind: "noop", why: null };
  const sameArmy = carry.army === tap.army;
  if (sameArmy && carry.slot === tap.slot) return { kind: "noop", why: null };
  if (carry.count === "ask") {
    if (sameArmy && (to.slots[tap.slot] ?? null) !== null) {
      return { kind: "noop", why: "Pick an empty socket to split into." };
    }
    return { kind: "ask", from: carry, to: tap };
  }
  const cmd = armyDropCommand({
    fromArmy: from, fromSlot: carry.slot, toArmy: to, toSlot: tap.slot, single: false,
    ...(carry.count === null ? {} : { count: carry.count }),
  });
  return cmd === null ? { kind: "noop", why: null } : { kind: "command", cmd };
}

export const armySlotCount = (a: ArmyState | null): number => a?.slots.length ?? 7;

export const totalStacks = (a: ArmyState | null): number => {
  if (a === null) return 0;
  let n = 0;
  for (const s of a.slots) if (s !== null) n++;
  return n;
};
